import { FC, useEffect, useState } from 'react';
import { mixpanelReset } from 'utils';
import { useNavigate } from 'react-router-dom';
import { coreService, platform } from 'network-utils';
import { useSelector } from 'react-redux';
import { MenuItems } from 'learning-panel-components';
import {
    selectToken,
    selectUserUuid,
    selectIsLeanUser,
    selectIsPLG,
    selectIsTournamentsGamePackageEnabled
} from '@redux/slices/userSlice';
import { useGetUserProfilePicture } from '@queries/platform/aws';
import { useGetUserInfo } from '@queries/platform/login';
import { getHasNewNotificationsKey, useGetHasNewNotifications } from '@queries/notifications/notifications';
import { useGetUpcomingTournaments } from '@queries/tournaments/tournament';
import { useGetCoreUpcomingTournaments } from '@queries/core/tournaments';
import { useGetUserAchievements } from '@queries/platform/achievements';
import { useGetUsersLeaderboards } from '@queries/core/homepage';
import { useGetLearningPaths } from '@queries/core/learningPath';
import eventBus from '@services/eventBus';
import { useQueryClient } from 'react-query';
import TopBar from './topBar';
import AdminTopBar from '../../adminPanel/topBar/topBar';

const TopBarContainer: FC<TopBarContainerProps> = ({
    selectedTab,
    setSelectedTab,
    setIsAccountSettingsOpen,
    setIsContactSupportOpen,
    setVideoTutorialUuid,
    isAdmin = false,
    onSurveyClick,
    onSwitchBack,
    onShowDownloadHubExtended,
    downloadHubAlert,
    closeDownloadHubAlert
}: TopBarContainerProps) => {
    const loginApi = platform.LoginApi;
    const queryClient = useQueryClient();
    const [hasNewNotifications, setHasNewNotifications] = useState<boolean>(false);
    const [username, setUserName] = useState<string>('');
    const userUuidId = useSelector(selectUserUuid);
    const token = useSelector(selectToken);
    const userUuid = useSelector(selectUserUuid);
    const isLeanUser = useSelector(selectIsLeanUser);
    const isPLG = useSelector(selectIsPLG);
    const isTournamentPackageEnabled = useSelector(selectIsTournamentsGamePackageEnabled);
    const navigate = useNavigate();
    const [img, setImg] = useState<string>('');
    const [shouldPlayGlow, setShouldPlayGlow] = useState<boolean>(false);
    const [devCoins, setDevCoins] = useState<number>(0);
    const [userRank, setUserRank] = useState<number>(0);
    const [learningPathProgress, setLearningPathProgress] = useState<{
        uuid: string;
        progress: number;
        name: string;
    }>();

    const { isError: isErrorUserProfilePicture, data: userProfilePicture } = useGetUserProfilePicture(userUuidId);
    const { isLoading: usersLeaderboardIsLoading, data: usersLeaderboardsData } = useGetUsersLeaderboards(token, {
        search: undefined,
        offset: 0,
        limit: 0,
        enableQuery: isLeanUser && !isPLG
    });

    const { data: userInfoData } = useGetUserInfo(userUuidId);
    const { data: newNotificationsResponse } = useGetHasNewNotifications(token);
    const { data: upcomingTournamentsData } = useGetUpcomingTournaments(token, isTournamentPackageEnabled);
    const { data: upcomingCoreTournamentsData } = useGetCoreUpcomingTournaments(token, isTournamentPackageEnabled);
    const { data: userAchievementsData } = useGetUserAchievements(userUuid, isLeanUser);
    const { data: learningPathsData } = useGetLearningPaths(token);

    useEffect(() => {
        if (!learningPathsData) return;
        if ((learningPathsData as any).success === false) return;
        const learningPath = learningPathsData.find((lp: any) => lp.badge !== null);
        learningPath &&
            setLearningPathProgress({
                uuid: learningPath?.uuid,
                progress: learningPath?.progress?.percent || 0,
                name: learningPath?.badge || ''
            });
    }, [learningPathsData]);

    useEffect(() => {
        if (upcomingTournamentsData && upcomingTournamentsData.success && !isTournamentPackageEnabled) {
            upcomingTournamentsData.tournaments.forEach((tournament: any) => {
                if (tournament.canJoin && (tournament.status === 'pending' || tournament.status === 'inProgress')) {
                    setShouldPlayGlow(true);
                }
            });
        } else if (upcomingCoreTournamentsData && upcomingCoreTournamentsData.success && isTournamentPackageEnabled) {
            upcomingCoreTournamentsData?.tournaments?.forEach((tournament: any) => {
                if (tournament.canJoin && (tournament.status === 'pending' || tournament.status === 'inProgress')) {
                    setShouldPlayGlow(true);
                }
            });
        }
    }, [upcomingTournamentsData, upcomingCoreTournamentsData, isTournamentPackageEnabled]);

    useEffect(() => {
        if (newNotificationsResponse?.json?.success) {
            setHasNewNotifications(newNotificationsResponse?.json.hasNew);
        }
    }, [newNotificationsResponse]);

    useEffect(() => {
        if (userInfoData) {
            setUserName(userInfoData?.nickname);
        }
    }, [userInfoData]);

    useEffect(() => {
        const hasProfilePicture = userProfilePicture?.url && !isErrorUserProfilePicture;
        setImg(
            hasProfilePicture ? userProfilePicture.url : `${process.env.PUBLIC_URL}/images/defaultProfilePicture.svg`
        );
    }, [userProfilePicture, isErrorUserProfilePicture]);

    useEffect(() => {
        const callback = (data: { type: string }) => {
            if (['reconnected', 'hasNewNotifications'].includes(data.type)) {
                queryClient.invalidateQueries([getHasNewNotificationsKey, token]);
            }
        };

        eventBus.on('notification', callback);

        return () => {
            eventBus.remove('notification', callback);
        };
    }, [queryClient, token]);

    useEffect(() => {
        if (!userAchievementsData || userAchievementsData?.user_points === undefined) return;

        setDevCoins(userAchievementsData.user_points);
    }, [userAchievementsData]);

    useEffect(() => {
        if (!usersLeaderboardsData) return;
        if ((usersLeaderboardsData as any).success === false) return;

        setUserRank(usersLeaderboardsData.currentUser.rank);
    }, [usersLeaderboardsData]);

    const onLogout = async () => {
        await loginApi.logout();
        mixpanelReset();
        navigate('/app/login');
    };

    return isAdmin ? (
        <AdminTopBar
            imageSrc={img}
            userName={username}
            onLogout={onLogout}
            setIsContactSupportOpen={setIsContactSupportOpen}
            newNotification={hasNewNotifications}
            onSurveyClick={onSurveyClick}
            onSwitchBack={onSwitchBack}
            onShowDownloadHubExtended={onShowDownloadHubExtended}
            downloadHubAlert={downloadHubAlert}
            closeDownloadHubAlert={closeDownloadHubAlert}
        />
    ) : (
        <TopBar
            setVideoTutorialUuid={setVideoTutorialUuid}
            imageSrc={img}
            userName={username}
            onLogout={onLogout}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            setIsAccountSettingsOpen={setIsAccountSettingsOpen}
            setIsContactSupportOpen={setIsContactSupportOpen}
            newNotification={hasNewNotifications}
            shouldPlayGlow={shouldPlayGlow}
            isLeanUser={isLeanUser}
            isPLG={isPLG}
            devCoins={devCoins}
            userRank={userRank}
            learningPathTopBadgeData={learningPathProgress}
        />
    );
};

interface TopBarContainerProps {
    selectedTab: keyof typeof MenuItems;
    setSelectedTab: Function;
    setIsAccountSettingsOpen: Function;
    setIsContactSupportOpen: Function;
    setVideoTutorialUuid: Function;
    isAdmin?: boolean;
    onSurveyClick?: Function;
    onSwitchBack?: Function;
    onShowDownloadHubExtended?: Function;
    downloadHubAlert?: {
        message: string;
        actionIcon?: string;
        actionMessage?: string;
        action?: Function;
    };
    closeDownloadHubAlert?: Function;
}

export default TopBarContainer;
